/*eslint no-fallthrough: ["error", { "commentPattern": "break[\\s\\w]*omitted" }]*/

import React, {
  useState,
  useEffect,
  useRef,
  FC,
  ReactNode,
  MutableRefObject,
  useCallback,
} from "react";
import Spinner from "react-spinkit";

import "./styles.scss";

import {
  Messages,
  IndicatorOptions,
  InternalOptions,
  TCameraOptions,
  ResponseTypeMap,
  EligibilityResponseTypeMap,
  ProgressInfoSnake,
  ProgressInfoResponseTypeMap,
  ProgressInfoCamel,
} from "../types/scanview.types";
import { GlobalLogger } from "./debugLogger";

import { defaultIndicatorOptions, defaultMessages } from "./scanview.data";

import detectorWrapper from "./detector";
import DebugMenu from "./debugMenu";
import ScanFrame from "./scanFrame";
import ScanVideoViewer from "./scanVideoViewer";
import CardCapture from "./cardCapture";
import ScanCanvas from "./scanCanvas";

import useCustomWidget from "./hooks/useCustomWidget";
import useLayoutHeight from "./hooks/useLayoutHeight";
import useBufferSize from "./hooks/useBufferSize";
import useVideoSizes from "./hooks/useVideoSizes";
import useRect from "./hooks/useRect";
import useInterval from "./hooks/useInterval";
import { MyGlobalContext } from "./ScanViewerContext";

import { isMobile } from "react-device-detect";
import { useMachine } from "@xstate/react";
import scanViewStateMachine from "./scanViewStateMachine";

import { scanHandler } from "./scanCanvasScanHelper";

import { _handleError } from "./scanViewErrorHandlerHelper";

import { cleanUp } from "./scanViewCleanUpHelper";

import { webSocketHandler } from "./scanViewWebSocketHelper";

import { resetCorners } from "./scanViewUIHelper";

import CameraButton from "../assets/camera_button.png";

import { CardScanLogoURL, getHeaderTitle } from "./helper";

import styles from "./scanview.module.scss";
import { cardScanPackageVersion } from "../version";
import {
  getDeviceInfo,
  getEnvironmentInfo,
  getRefSize,
} from "../utils/getInfoFunctions";

import {
  CameraPermissionModalConfig,
  ErrorModalT,
} from "../types/Modals/ErrorModal";
import ScanVideoContextProvider from "./contexts/ScanVideoContext";
import ErrorModal from "../components/ErrorModal/ErrorModal";
import { HelpModalConfig } from "../components/ErrorModal/components/HelpModal/HelpModal";
import { RequestPermissionModalConfig } from "../components/RequestingPermissionModal/RequestingPermissionModal";
import { NoCameraModalConfig } from "../types/Modals/NoCameraModal";
import { setInLocalStorage } from "./helpers/localStorageHelper";
import { ModelPrediction } from "./types/Model";
import {
  CardApiResponseSnake,
  CardScanApi,
  CardScanError,
  CardState,
  EligibilityApiResponseSnake,
  EligibilityInfo,
  LogLevel,
  NameCase,
} from "../types/types";
import { ValidationError } from "yup";
import { WebToMobileHandOffConfig } from "../components/ErrorModal/components/WebToMobile/WebToMobile";
import AriaLiveRegion from "../components/AriaLiveRegion/AriaLiveRegion";
import AdaptiveThreshold from "../utils/AdaptiveThreshold";
import { ScanGuidance, ScanGuidanceUI } from "../utils/ScanGuidance";

const DEV_SERVER =
  process.env && process.env.REACT_APP_CARDSCAN_SERVER
    ? process.env.REACT_APP_CARDSCAN_SERVER
    : undefined;

const DEV_WEBSOCKET_SERVER =
  process.env && process.env.REACT_APP_CARDSCAN_WEBSOCKET_SERVER
    ? `wss://${process.env.REACT_APP_CARDSCAN_WEBSOCKET_SERVER}`
    : undefined;

declare global {
  interface Window {
    Cypress?: any;
  }
}

const CardScanView = <Name extends NameCase = "snake">(
  props: CardScanViewProps<Name>
): React.ReactElement => {
  const {
    logging,
    live = false,
    sessionToken,
    onSuccess,
    onEligibilitySuccess,
    onEligibilityError,
    onProgress,
    onScanStart,
    onCancel,
    onError,
    onRetry,
    cameraPermissionModalConfig,
    fullScreen = true,
    backsideSupport = false,
    eligibility,
    messages = {},
    closeButton,
    successIndicator,
    errorIndicator,
    indicatorOptions,
    autoSwitchActiveColor,
    autoSwitchInactiveColor,
    progressBarColor,
    searchingBarColor,
    toastBackgroundColor,
    toastTextColor,
    widgetBackgroundColor,
    messageFontSize,
    messageTextColor,
    messageBackgroundColor,
    requestPermissionModalConfig,
    noCameraModalConfig,
    helpModalConfig,
    webToMobileHandoffConfig = {
      enabled: false,
      urlBase: live
        ? "https://capture.cardscan.ai"
        : "https://sandbox-capture.cardscan.ai",
      companyName: "CardScanHealth",
    },
    _internal,
    cameraOptions = {},
    autoScanTimeout,
  } = props;

  const internalOptions: InternalOptions = {
    nameCase: "camel",
    ..._internal,
  };
  //video, detection and image capture canvas
  const videoRefBg = useRef<HTMLVideoElement>(null);
  const detectionCanvasRef = useRef<HTMLCanvasElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement>(null);
  const isCardCreationInProgress = useRef(false);
  const [frameDivRefRect, frameDivRef] = useRect();
  const [isMobileOnLandscape, setIsMobileOnLandscape] = useState(false);
  const [isVideoPortrait, setIsVideoPortrait] = useState(false);
  const scanCountRef = useRef(0) as MutableRefObject<number>;
  const performanceCountRef = useRef(0) as MutableRefObject<number>;
  const [webToMobileTimer, setWebToMobileTimer] = useState<number | null>(
    webToMobileHandoffConfig?.autoRedirect || null
  );
  const hasLogged = useRef(false);
  const [ariaLiveMessage, setAriaLiveMessage] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);
  const changeAriaLiveMessage = (message: string) => {
    setAriaLiveMessage(message);
  };
  const scannerMainContainerRef = useRef<HTMLDivElement>(null);

  const metricsDumpRef = useRef<string[]>([]);

  const scanGuidance = useRef(new ScanGuidance());
  const guidanceUI = useRef(
    new ScanGuidanceUI({
      highDetail: 0.15,
    })
  );

  const [scanningGuideMessage, setScanningGuideMessage] = useState("");
  const [guideColor, setGuideColor] = useState<"notDetected" | "detected">(
    "notDetected"
  );

  useEffect(() => {
    if (logging) {
      setModelState(detectorWrapper.get().modelState);
    }

    if (live) {
      GlobalLogger.setLogLevel(
        logging === undefined || logging === "off" ? "off" : "normal"
      );
    } else {
      GlobalLogger.setLogLevel(logging || "off");
    }
  }, [logging, live, detectorWrapper]);

  useEffect(() => {
    const checkOrientation = () => window.innerWidth > window.innerHeight;

    const updateOrientation = () => {
      setIsMobileOnLandscape(checkOrientation() && isMobile);
    };

    updateOrientation();

    window.addEventListener("resize", updateOrientation);

    return () => {
      window.removeEventListener("resize", updateOrientation);
    };
  }, [isMobile]);

  const cardScanApi = useRef<CardScanApi | null>(null);

  //TODO: remove this and move to state machine?
  const cardIdRef = useRef("");
  const eligibilityIdRef = useRef("");

  useEffect(() => {
    let apiEndpoint = DEV_SERVER;
    let wsEndpoint = DEV_WEBSOCKET_SERVER;
    if (internalOptions?.devAPIEndpoint) {
      apiEndpoint =
        typeof internalOptions.devAPIEndpoint === "string"
          ? internalOptions.devAPIEndpoint
          : "https://dev.nop.sh/v1";
    }

    if (internalOptions?.devWSEndpoint) {
      wsEndpoint =
        typeof internalOptions.devWSEndpoint === "string"
          ? internalOptions.devWSEndpoint
          : "wss://dev-ws.nop.sh";
    }

    if (
      window.Cypress &&
      window.Cypress.env("cardScanWebsocketUrl") &&
      window.Cypress.env("cardScanWebsocketUrl") !== "NONE"
    ) {
      wsEndpoint = window.Cypress.env("cardScanWebsocketUrl");
    }

    if (hasLogged.current === false) {
      GlobalLogger.debug(`Using package v${cardScanPackageVersion}`, "verbose");

      if (
        internalOptions?.webviewVersion ||
        internalOptions?.mobileSdkVersion
      ) {
        const versions = [];
        if (internalOptions?.mobileSdkVersion) {
          versions.push(`mobileSdk v${internalOptions.mobileSdkVersion}`);
        }
        if (internalOptions?.webviewVersion) {
          versions.push(`webview v${internalOptions.webviewVersion}`);
        }
        GlobalLogger.debug(`Using ${versions.join(" with ")}`, "verbose");
      }

      GlobalLogger.debug(
        `Using API Endpoint: ${apiEndpoint} and WS Endpoint: ${wsEndpoint}`
      );

      hasLogged.current = true;
    }

    if (sessionToken && !cardScanApi.current) {
      const headers: Record<string, string> = {
        "x-cardscan-ai-sdk": `insurance_cardscan_react ${cardScanPackageVersion}`,
      };

      if (internalOptions?.webviewVersion) {
        headers[
          "x-webview-version"
        ] = `shared_react_webview ${internalOptions.webviewVersion}`;
      }

      if (internalOptions?.mobileSdkVersion) {
        headers[
          "x-mobile-sdk-version"
        ] = `sdk_version ${internalOptions.mobileSdkVersion}`;
      }

      cardScanApi.current = new CardScanApi({
        apiKey: sessionToken,
        environment: live ? "production" : "sandbox",
        basePath: apiEndpoint,
        websocketUrl: wsEndpoint,
        logging: logging
          ? logging === "everything" || logging === "verbose"
            ? "DEBUG"
            : "ERROR"
          : "ERROR",
        preInitializeWebsocket: true,
        baseOptions: {
          headers,
        },
      });
    }
  }, [sessionToken, live, internalOptions]);

  // Update user UI
  const [showCurtain, setShowCurtain] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState<ErrorModalT>({
    show: false,
    modal: "none",
  });

  const [messageState] = useState({
    ...defaultMessages,
    ...messages,
  });

  const [spinnerOptions] = useState({
    ...defaultIndicatorOptions,
    ...indicatorOptions,
  });

  const [stateMachineState, stateMachineSend, stateMachineService] = useMachine(
    scanViewStateMachine,
    {
      context: {
        messageState: messageState,
      },
    }
  );

  //updates the header title on every transition
  stateMachineService.subscribe((state) => {
    stateMachineState.context.headerTitle = getHeaderTitle(
      String(state.value),
      messageState,
      stateMachineState.context.autoScan,
      stateMachineState.context.scannerRunning
    );
  });

  // % of progress in scanning process
  const [scanProgress, setScanProgress] = useState(0.0);
  // Also set server side for max successful scans.
  const softMaxUploads = 12;

  // Card detection parameters & metadata
  const adaptiveThreshold = useRef(
    new AdaptiveThreshold({ ...internalOptions.adaptiveThresholdConfig })
  );
  if (process.env.NODE_ENV === "development") {
    (window as any).adaptiveThreshold = adaptiveThreshold.current;
  }
  const defaultScanFrequency = 250;
  const [scanFreqency, setScanFreqency] = useState(defaultScanFrequency);
  const prevScanning = useRef(false);
  const prevSide = useRef<"front" | "back">("front");

  // state to save timestamp of last WS ping
  const pingFrequency = 6500;
  const [lastPing, setLastPing] = useState<Date>(new Date());

  const [videoMetadata, setVideoMetadata] = useState<{ [key: string]: any }>(
    {}
  );

  // Short lived buffer for storing scan images before upload
  const [bufferSize, setBufferSize] = useBufferSize(4);

  const scanCaptureBuffer = useRef<
    { file: File; score: number; laplacian: number }[]
  >([]);

  //debug options
  const [latestScore, setLatestScore] = useState(0.0);
  const [latestLaplacian, setLatestLaplacian] = useState(0.0);
  const [bestScore, setBestScore] = useState(0.0);
  const [bestClass, setBestClass] = useState<ModelPrediction | null>(null);
  const [lastClass, setLastClass] = useState("");
  const [bestLaplacian, setBestLaplacian] = useState(0.0);
  const debugCanvasBestRef = useRef<HTMLCanvasElement>(null);
  const debugCanvasLatestRef = useRef<HTMLCanvasElement>(null);
  const [modelState, setModelState] = useState("");
  const [scanDebugState, setScanDebugState] = useState("");
  const topPredictions: MutableRefObject<ModelPrediction[]> = useRef([]);
  const isEligibilityCreationInProgress = useRef(false); // needed to not create double eligibility

  useVideoSizes(videoRefBg, setShowCurtain, stateMachineState);

  let videoWidth = videoRefBg?.current?.videoWidth
    ? videoRefBg?.current?.videoWidth
    : 500;

  let videoHeight = videoRefBg?.current?.videoHeight
    ? videoRefBg?.current?.videoHeight
    : 500;

  const scale = Math.sqrt(isMobile ? 0.95 : 0.65); // This scales the area to 95% or 65% of the original

  videoWidth *= scale;
  videoHeight *= scale;

  //A card's usual aspect ratio
  //85.60 × 53.98 mm (3.370 × 2.125 in) with an aspect ratio of 1.586
  const CARD_ASPECT_RATIO = 1.58;
  const device_aspect_ratio: number = videoHeight / videoWidth;

  //if height is much larger than width
  //we fix the dimensions to fit in a regular card's usual aspect ratio
  if (device_aspect_ratio >= CARD_ASPECT_RATIO) {
    videoHeight = videoHeight / device_aspect_ratio;
    videoWidth = videoWidth * CARD_ASPECT_RATIO;
  }

  let videoLayoutHeight =
    videoRefBg?.current?.getBoundingClientRect()?.height || 0;
  videoLayoutHeight =
    videoLayoutHeight > videoHeight ? videoHeight : videoLayoutHeight;

  useLayoutHeight(
    videoRefBg,
    stateMachineState.matches("cameraLoading"),
    videoLayoutHeight
  );

  useCustomWidget({
    autoSwitchActiveColor,
    autoSwitchInactiveColor,
    progressBarColor,
    widgetBackgroundColor,
    messageFontSize,
    messageTextColor,
    messageBackgroundColor,
    cameraOptions,
    toastBackgroundColor,
    toastTextColor,
    searchingBarColor,
  });

  const getCardMetadata = useCallback(() => {
    const environmentInfo = getEnvironmentInfo();
    const deviceInfo = getDeviceInfo();
    const videoElementInfo = getRefSize(videoRefBg);
    const guideFrameElementInfo = getRefSize(frameDivRef);

    return {
      environmentInfo,
      deviceInfo,
      videoElementInfo,
      guideFrameElementInfo,
      videoMetadata,
      mobile: isMobile,
      ...(internalOptions?.webviewVersion
        ? { webviewVersion: internalOptions.webviewVersion }
        : {}),
      ...(internalOptions?.mobileSdkVersion
        ? { mobileSdkVersion: internalOptions.mobileSdkVersion }
        : {}),
    };
  }, [videoMetadata, frameDivRef, videoRefBg]);

  const getPerformanceData = useCallback(() => {
    const environmentInfo = getEnvironmentInfo();
    const videoElementInfo = getRefSize(videoRefBg);
    const guideFrameElementInfo = getRefSize(frameDivRef);

    return {
      mobile: isMobile,
      browser: {
        name: environmentInfo.browser,
        version: environmentInfo.browserVersion,
      },
      video: videoElementInfo,
      guide: guideFrameElementInfo,
      deviceId: videoMetadata?.videoTrack?.deviceId || "unknown",
      groupId: videoMetadata?.videoTrack?.groupId || "unknown",
      sdkVersion: cardScanPackageVersion,
    };
  }, [videoMetadata, frameDivRef, videoRefBg]);

  const checkEligibility = useCallback(async () => {
    GlobalLogger.log("checkEligibility init", "verbose");
    if (eligibility && cardIdRef.current) {
      GlobalLogger.log("runnning checkEligibility validation");

      GlobalLogger.log(
        `Eligibility: ${JSON.stringify(eligibility)}`,
        "verbose"
      );
      try {
        const eligibilityValidation = cardScanApi.current?.validateEligibility(
          cardIdRef.current,
          eligibility
        );
      } catch (error) {
        stateMachineSend("eligibilityError");
        GlobalLogger.log(`Eligibility Error: ${error}`, "verbose");
        if (error instanceof Error && error.name === "ValidationError") {
          const validationError = error as ValidationError;
          if (onEligibilityError) {
            _handleError(
              validationError,
              stateMachineSend,
              onEligibilityError,
              stateMachineState,
              "eligibility"
            );
          }
        }
      }
    }
  }, [eligibility, cardIdRef.current]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  //This is the main scanning loop and is enabled once the video has loaded.
  //Toggable with loop duration is set via scanFrequency.
  useInterval(
    () => {
      scanHandler(
        videoRefBg,
        frameDivRef,
        captureCanvasRef,
        detectionCanvasRef,
        debugCanvasLatestRef,
        debugCanvasBestRef,
        scanCountRef,
        performanceCountRef,
        topPredictions,
        getPerformanceData,
        setLatestScore,
        setLatestLaplacian,
        setBestScore,
        setBestClass,
        setLastClass,
        setBestLaplacian,
        setModelState,
        setScanDebugState,
        setScanProgress,
        cameraOptions.flipHorizontal,
        cameraOptions.flipVertical,
        scanCaptureBuffer,
        adaptiveThreshold.current,
        onError,
        scanProgress,
        softMaxUploads,
        bufferSize,
        videoMetadata,
        cardScanApi,
        cardIdRef,
        stateMachineState,
        stateMachineSend,
        metricsDumpRef,
        guidanceUI,
        scanGuidance,
        handleGuidanceUI,
        live ? false : internalOptions?.debugMenu || false
      );
      wsPing(); //this should be moved to a separate interval
    },
    stateMachineState.context.scannerRunning ? scanFreqency : null
  );

  const handleGuidanceUI = (
    newGuideMessage: string,
    guideColor: "notDetected" | "detected"
  ) => {
    setScanningGuideMessage(newGuideMessage);
    setGuideColor(guideColor);
  };
  // Reset lastEvents and lastHashes in every start up
  useEffect(() => {
    setInLocalStorage("lastEvents", []);
    setInLocalStorage("lastHashes", []);
  }, []);

  useEffect(() => {
    if (
      stateMachineState.context.autoScan &&
      !stateMachineState.context.processing
    ) {
      if (autoScanTimeout !== undefined) {
        const timer = setTimeout(() => {
          stateMachineSend("AUTO_TIMED_OUT");
        }, autoScanTimeout * 1000);

        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [
    stateMachineState.context.autoScan,
    stateMachineState.context.processing,
    autoScanTimeout,
    stateMachineSend,
  ]);

  useEffect(() => {
    if (
      isMobile &&
      !isMobileOnLandscape &&
      videoRefBg.current &&
      videoRefBg.current.videoWidth < videoRefBg.current.videoHeight
    ) {
      setIsVideoPortrait(true);
    } else {
      setIsVideoPortrait(false);
    }
  }, [
    isMobile,
    isMobileOnLandscape,
    videoRefBg.current?.videoWidth,
    videoRefBg.current?.videoHeight,
  ]);

  useEffect(() => {
    webSocketHandler(
      cardScanApi,
      cardIdRef,
      onError,
      stateMachineState,
      stateMachineSend,
      backsideSupport,
      eligibility ? true : false,
      onEligibilityError
    );
  }, [cardScanApi, stateMachineState, stateMachineSend, stateMachineService]);

  useEffect(() => {
    const scanning = stateMachineState.context.scannerRunning;
    const side: "front" | "back" = [
      "backsidePending",
      "backsideProcessing",
    ].some(stateMachineState.matches)
      ? "back"
      : "front";

    if (!prevScanning.current && scanning) {
      adaptiveThreshold.current.startSession(side); // open side
    } else if (prevScanning.current && !scanning) {
      adaptiveThreshold.current.stopSession(); // side closed
    } else if (prevScanning.current && scanning && prevSide.current !== side) {
      adaptiveThreshold.current.startSession(side); // front → back flip
    }

    prevScanning.current = scanning;
    prevSide.current = side;
  }, [stateMachineState, adaptiveThreshold]);

  useEffect(() => {
    return () => {
      adaptiveThreshold.current.stopSession();
    };
  }, []);

  //TODO: find a way to reset states more clearly
  useEffect(() => {
    const subscription = stateMachineService.subscribe((state) => {
      GlobalLogger.debug(
        `state machine state: ${state.value} - ${state.event.type}`
      );

      if (state.event.type === "scan.completed" && cardScanApi.current) {
        const infoCamel: ProgressInfoCamel = {
          cardId: state.context.card_id,
          cardState: state.value as CardState,
          scanCount: scanCountRef.current,
        };

        let info: ProgressInfoResponseTypeMap[Name];
        if (internalOptions.nameCase === "camel") {
          info = infoCamel as ProgressInfoResponseTypeMap[Name];
        } else {
          const infoSnake =
            cardScanApi.current.toSnakeCase<ProgressInfoSnake>(infoCamel);
          info = infoSnake as ProgressInfoResponseTypeMap[Name];
        }

        if (onProgress) {
          onProgress(info);
        }
      }

      if (state.matches("success")) {
        if (showErrorModal.show === true) {
          setShowErrorModal({ show: false, modal: "none" });
        }
        cardScanApi?.current
          ?.getCardById(cardIdRef.current)
          .then((res) => {
            const card = res;

            GlobalLogger.log(`scanning completed: c:${card.cardId}`);
            GlobalLogger.debug(
              `scanned card: ${JSON.stringify(card)}`,
              "everything"
            );

            let result: ResponseTypeMap[Name];

            if (internalOptions.nameCase === "camel") {
              result = card as ResponseTypeMap[Name];
            } else {
              if (cardScanApi.current) {
                const cardCamel =
                  cardScanApi.current.toSnakeCase<CardApiResponseSnake>(card);
                result = cardCamel as ResponseTypeMap[Name];
              } else {
                result = card as ResponseTypeMap[Name];
              }
            }

            onSuccess(result);
          })
          .then(() => {
            if (eligibilityIdRef.current && onEligibilitySuccess) {
              cardScanApi.current
                ?.getEligibilityById(eligibilityIdRef.current)
                .then((res) => {
                  GlobalLogger.log(
                    `eligibility completed: e:${eligibilityIdRef.current}`
                  );
                  GlobalLogger.debug(
                    `eligibility payload: ${JSON.stringify(res)}`,
                    "everything"
                  );
                  const eligibility = res;

                  let result: EligibilityResponseTypeMap[Name];

                  if (internalOptions.nameCase === "camel") {
                    result = eligibility as EligibilityResponseTypeMap[Name];
                  } else {
                    if (cardScanApi.current) {
                      const eligibilitySnake =
                        cardScanApi.current.toSnakeCase<EligibilityApiResponseSnake>(
                          eligibility
                        );
                      result =
                        eligibilitySnake as EligibilityResponseTypeMap[Name];
                    } else {
                      result = eligibility as EligibilityResponseTypeMap[Name];
                    }
                  }
                  if (eligibility.state !== "error")
                    onEligibilitySuccess(result);
                });
            }
          })
          .finally(() => {
            cleanUp(
              scanCaptureBuffer,
              setScanProgress,
              videoRefBg,
              cardScanApi
            );
          });
      }

      if (
        ["error", "retry"].some(state.matches) &&
        webToMobileHandoffConfig?.enabled === true &&
        !isMobile
      ) {
        setShowErrorModal({ show: true, modal: "webToMobile" });
      }

      //reset before moving to backside
      if (state.matches("backsidePending")) {
        scanCountRef.current = 0;
        scanCaptureBuffer.current = [];
        setGuideColor("notDetected");
        setScanProgress(0);
        resetCorners();
        scanGuidance.current.reset();
        GlobalLogger.debug("Flipping to backside scan");
      }

      if (
        state.matches("eligibilityPending") &&
        state.event.type === "card.completed" &&
        eligibility &&
        !isEligibilityCreationInProgress.current
      ) {
        isEligibilityCreationInProgress.current = true;
        cardScanApi.current
          ?.createEligibility({
            eligibility: eligibility,
            card_id: cardIdRef.current,
          })
          .then((response) => {
            eligibilityIdRef.current = response.eligibilityId;
          })
          .catch((error) => {
            if (onEligibilityError) {
              _handleError(
                error,
                stateMachineSend,
                onEligibilityError,
                stateMachineState,
                "eligibility"
              );
            }
            isEligibilityCreationInProgress.current = false;
          });
      }

      // on toggle
      if (
        state.context.toggledAutoManual &&
        ["autoCapture", "manualCapture"].some(state.matches)
      ) {
        GlobalLogger.debug("Toggling to manual capture");
        scanCaptureBuffer.current = [];
        setScanProgress(0);
        document.documentElement.style.setProperty(
          "--cardscan-buffer-size",
          "0"
        );
        setScanFreqency(defaultScanFrequency);
      }
    });

    return subscription.unsubscribe;
  }, [stateMachineService, showErrorModal]);

  useEffect(() => {
    const shouldInitiateCardCreation =
      videoMetadata &&
      frameDivRef.current &&
      videoRefBg.current &&
      stateMachineState.context.modelState === "warmed";

    if (!isCardCreationInProgress.current && shouldInitiateCardCreation) {
      isCardCreationInProgress.current = true;

      const metadata = getCardMetadata();

      cardScanApi.current
        ?.createCard({
          enable_backside_scan: backsideSupport,
          enable_livescan: true,
          metadata: metadata,
        })
        .then((response) => {
          cardIdRef.current = response.cardId;
          GlobalLogger.log(`Card ID: ${cardIdRef.current}`);

          onScanStart?.(cardIdRef.current, false);

          // register socket on card creation
          if (
            cardScanApi.current?.websocket &&
            cardScanApi.current?.websocket.readyState === 1
          ) {
            const payload = JSON.stringify({
              card_id: response.cardId,
              action: "register",
              source: "startup",
            });
            cardScanApi.current?.websocket.send(payload);
          }
        })
        .catch((error) => {
          _handleError(error, stateMachineSend, onError);
          isCardCreationInProgress.current = false;
        });
    }
  }, [
    cardScanApi,
    stateMachineSend,
    onError,
    backsideSupport,
    stateMachineState,
    videoMetadata,
    frameDivRef.current,
    videoRefBg.current,
  ]);

  useEffect(() => {
    // Setup popstate event listener
    const popstateHandler = () => {
      cleanUp(scanCaptureBuffer, setScanProgress, videoRefBg, cardScanApi);
    };
    window.addEventListener("popstate", popstateHandler);

    // Cleanup popstate event listener on component unmount
    return () => {
      window.removeEventListener("popstate", popstateHandler);
    };
  }, []);

  //TODO: move this into cardScanApi w/ own timer and toggle on/off?
  const wsPing = () => {
    const now = new Date();
    const ms = lastPing.getTime();

    if (["success", "error"].some(stateMachineState.matches)) {
      return;
    }

    if (now.getTime() - ms < pingFrequency) {
      return;
    }

    if (
      cardScanApi.current?.websocket &&
      cardScanApi.current?.websocket.readyState == 1
    ) {
      cardScanApi.current?.websocket.send("ping");
      setLastPing(now);
    }
  };

  //---------------------------------------
  // UI Interaction handlers
  //---------------------------------------

  const showCameraButton =
    (!stateMachineState.context.autoScan &&
      !stateMachineState.context.processing) ||
    (stateMachineState.context.autoScan &&
      stateMachineState.context.showCameraButton &&
      !stateMachineState.context.processing);

  //TODO: most of this could be reduced to state machine (but need to add retry state -> autocapture)
  const handleRetryButton = async () => {
    //sanity check we're in retry mode.

    //empty both detection buffer and clear progress UI
    setShowErrorModal({ show: false, modal: "none" });
    scanCaptureBuffer.current = [];
    scanCountRef.current = 0;
    scanGuidance.current.reset();
    metricsDumpRef.current = [];
    setScanProgress(0);
    document.documentElement.style.setProperty("--cardscan-buffer-size", "0");
    document.documentElement.style.setProperty(
      "--cardscan-corner-height",
      "20%"
    );
    document.documentElement.style.setProperty(
      "--cardscan-corner-width",
      "20%"
    );

    const video = videoRefBg.current;
    if (video?.paused) {
      await video?.play();
    }

    //reset state.
    stateMachineSend("retry_scan");
    setScanFreqency(defaultScanFrequency);
    cardIdRef.current = "";

    if (onRetry) {
      onRetry();
    }

    //de-dupe this
    cardScanApi.current
      ?.createCard({
        enable_backside_scan: backsideSupport,
        enable_livescan: true,
        // metadata
      })
      .then((response) => {
        cardIdRef.current = response.cardId;

        onScanStart?.(cardIdRef.current, false);
        //register socket on card creation
        if (
          cardScanApi.current?.websocket &&
          cardScanApi.current?.websocket.readyState == 1
        ) {
          // 1 is open
          const payload = JSON.stringify({
            card_id: response.cardId,
            action: "register",
            source: "startup",
          });
          cardScanApi.current?.websocket.send(payload);
        }

        //needs to implement another state coming to "retry"
        stateMachineSend("retry_scan");
      })
      .catch((error) => {
        _handleError(error, stateMachineSend, onError);
      });
  };

  const onCloseBtn = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const getModelState = (state: string) => {
    stateMachineSend({
      type: "UPDATE_MODEL_STATE",
      modelState: state,
    });
  };

  useEffect(() => {
    if (stateMachineState.value === "cameraLoading") {
      const initialModelState = detectorWrapper.get().modelState;

      stateMachineSend({
        type: "UPDATE_MODEL_STATE",
        modelState: initialModelState,
      });

      if (initialModelState !== "warmed") {
        detectorWrapper.get().setModelStatusCallback(getModelState);
      }
    }
  }, [stateMachineState.value]);

  // Reset corners on component mount and unmount
  useEffect(() => {
    resetCorners();

    return () => {
      resetCorners();
    };
  }, []);

  useEffect(() => {
    if (!modalRef.current) return;

    // Define the selector for focusable elements.
    const focusableSelectors =
      'a, button, textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusableElements =
        modalRef.current!.querySelectorAll<HTMLElement>(focusableSelectors);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    modalRef.current.addEventListener("keydown", handleKeyDown);

    const initialFocusableElements =
      modalRef.current.querySelectorAll<HTMLElement>(focusableSelectors);
    if (initialFocusableElements.length > 0) {
      initialFocusableElements[0].focus();
    }

    return () => {
      modalRef.current?.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  //---------------------------------------
  // Rendering
  //---------------------------------------

  return (
    <MyGlobalContext.Provider
      value={{
        logging: logging || GlobalLogger.startingLogLevel,
        adaptiveThreshold,
        modelState,
        cardScanApi,
        scanDebugState,
        fullScreen,
        scanFreqency,
        bufferSize,
        scanningGuideMessage,
        guideColor,
        metricsDumpRef,
        bufferRef: scanCaptureBuffer,
        debugCanvasBestRef,
        bestScore,
        bestClass,
        lastClass,
        bestLaplacian,
        debugCanvasLatestRef,
        latestScore,
        latestLaplacian,
        debugMenu: live ? false : internalOptions?.debugMenu || false,
        videoMetadata,
        showCurtain,
        closeButton,
        isMobile,
        isMobileOnLandscape,
        stateMachineState,
        stateMachineService,
        showErrorModal,
        cameraPermissionModalConfig,
        webToMobileHandoffConfig,
        videoRef: videoRefBg,
        backsideSupport: backsideSupport || false,
        requestPermissionModalConfig,
        helpModalConfig,
        noCameraModalConfig,
        setVideoMetadata,
        setScanFreqency,
        setBufferSize,
        onCloseBtn,
        stateMachineSend,
        handleRetryButton,
        setShowErrorModal,
        onError,
        setScanDebugState,
        webviewVersion: _internal?.webviewVersion ?? null,
        mobileSdkVersion: _internal?.mobileSdkVersion ?? null,
        webToMobileTimer,
        setWebToMobileTimer,
        changeAriaLiveMessage,
        scannerMainContainerRef,
        live,
        internalOptions,
        modalRef,
      }}
    >
      <AriaLiveRegion message={ariaLiveMessage} />
      <ScanVideoContextProvider>
        <ErrorModal />
        <div
          data-cy="cardscanMainContainer"
          className={`${styles.cardscanMain} ${
            isMobile ? styles.isMobile : ""
          } ${isMobileOnLandscape ? styles.isLandscape : ""}`}
          ref={scannerMainContainerRef}
        >
          <DebugMenu />
          <CardCapture>
            <div
              className={`${styles.cardscanVideoContainer} ${
                fullScreen && !isMobile ? styles.fullScreenLimit : ""
              } 
            ${isMobile ? styles.mobile : ""}
            ${isMobileOnLandscape ? styles.landscapeMobile : ""}
            ${isVideoPortrait ? styles.portraitView : ""}`}
            >
              <ScanVideoViewer
                isMobile={isMobile}
                isVideoPortrait={isVideoPortrait}
              />
              {stateMachineState.matches("cameraLoading") ? (
                <div
                  data-cy="cardScanCameraLoading"
                  className={styles.spinnerWrapper}
                >
                  <Spinner {...spinnerOptions} />
                </div>
              ) : (
                <ScanFrame
                  successIndicator={successIndicator}
                  errorIndicator={errorIndicator}
                  handleRetryButton={handleRetryButton}
                  frameDivRef={frameDivRef}
                  spinnerOptions={spinnerOptions}
                  webToMobileHandoffConfig={webToMobileHandoffConfig}
                  cardScanApi={cardScanApi}
                  backsideSupport={backsideSupport}
                />
              )}
              <img
                data-cy="cardscanLogo"
                alt="Scanning powered by CardScan dot AI"
                src={CardScanLogoURL}
                className={`${styles.cardscanLogo} ${
                  isMobile ? styles.cardscanLogoMobile : ""
                }`}
                draggable={false}
              />
              {!showCurtain &&
                !stateMachineState.matches("cameraLoading") &&
                showCameraButton && (
                  <>
                    <button
                      data-cy="cameraBtn"
                      className={`${styles.cameraBtn} ${
                        isMobile ? styles.mobile : ""
                      }`}
                      id="camera-btn"
                      onClick={() => stateMachineSend("MANUAL_BUTTON_CLICK")}
                      aria-label="Camera"
                    >
                      <img
                        src={CameraButton}
                        alt="Camera button"
                        draggable={false}
                      />
                    </button>
                  </>
                )}
            </div>
          </CardCapture>
        </div>
        <ScanCanvas
          captureCanvasRef={captureCanvasRef}
          detectionCanvasRef={detectionCanvasRef}
          frameDivDimensions={frameDivRefRect}
          frameDivRef={frameDivRef}
          videoRefBg={videoRefBg}
          videoHeight={videoHeight}
          videoWidth={videoWidth}
        />
      </ScanVideoContextProvider>
    </MyGlobalContext.Provider>
  );
};

//TODO: add required vs optional fields.
//Required - card fails without these, when it hits max scans.
//Optional - card keeps scanning if it has a high confidence version of one of these.
//         - does not fail on max scans without them.

export interface CardScanViewProps<Name extends NameCase = "snake"> {
  logging?: LogLevel;
  live?: boolean;
  sessionToken: string;
  fullScreen?: boolean;
  autoScanTimeout?: number;
  onSuccess: (detail: ResponseTypeMap[Name]) => void;
  onEligibilitySuccess?: (detail: EligibilityResponseTypeMap[Name]) => void;
  onEligibilityError?: (error: CardScanError) => void;
  onCancel?: () => void;
  onError?: (error: CardScanError) => void;
  onRetry?: () => void;
  onProgress?: (progressInfo: ProgressInfoResponseTypeMap[Name]) => void;
  onScanStart?: (cardId: string, isBackside: boolean) => void;
  messages?: Messages;
  backsideSupport?: boolean;
  eligibility?: EligibilityInfo;
  closeButton?: ReactNode;
  successIndicator?: ReactNode;
  errorIndicator?: ReactNode;
  indicatorOptions?: IndicatorOptions;
  cameraPermissionModalConfig?: CameraPermissionModalConfig;
  autoSwitchActiveColor?: string;
  autoSwitchInactiveColor?: string;
  progressBarColor?: string;
  searchingBarColor?: string;
  toastBackgroundColor?: string;
  toastTextColor?: string;
  widgetBackgroundColor?: string;
  messageFontSize?: string;
  messageTextColor?: string;
  messageBackgroundColor?: string;
  webToMobileHandoffConfig?: WebToMobileHandOffConfig;
  helpModalConfig?: HelpModalConfig;
  requestPermissionModalConfig?: RequestPermissionModalConfig;
  noCameraModalConfig?: NoCameraModalConfig;
  _internal?: InternalOptions;
  cameraOptions?: TCameraOptions;
}

export default CardScanView;
